import React, { useState } from 'react';
import Modal from '../common/Modal';
import { useProfileStore } from '../../state/profileStore';

const AddAssociateModal = ({ isOpen, onClose, businessEmail }) => {
  const user = useProfileStore((s) => s.profile);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Domain-match validation removed 2026-05-27. The original rule required
  // the invited associate's email domain to match the owner's domain, which
  // broke every signup path that doesn't have a real website domain on file:
  //   * GBP / OAuth (FB / IG / TikTok) signups — owner's email is often
  //     gmail.com, so the rule demanded every associate also be @gmail.com.
  //   * Spouse / partner with their own personal email domain.
  //   * Multi-domain parent/subsidiary structures.
  // The owner is already authenticated and is trusted to know who works
  // with them. The associate still has to verify the email they receive
  // before the account is usable, so an "invite to a random address" only
  // creates a dormant invite — no security exposure.

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Check email format first
    if (!validateEmail(email)) {
      setStatus({
        type: 'error',
        message: 'Please enter a valid email format.'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/register/associate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          associate_email: email,
          inviter_email: user.u_email
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send invitation');

      setStatus({ type: 'success', message: 'Invitation sent successfully!' });
      setEmail('');
      setTimeout(() => onClose(), 3000);
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h2 className="text-2xl font-bold text-[#00aef7] mb-4">Add Associate</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00aef7]"
              placeholder="Enter associate's email"
              required
            />
          </div>
          {status.message && (
            <div className={`mb-4 p-3 rounded ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {status.message}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-[#00aef7] rounded-md hover:bg-[#00aef7]/90 disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddAssociateModal;